import * as React from "react";
import { useState, useEffect } from "react";
import { SocialLinkInput } from "@/ui/components/ui/social-link-input";
import { VisibilityToggle } from "@/ui/components/ui/VisibilityToggle";
import {
  EmailIcon,
  PhoneIcon,
  XIcon,
  InstagramIcon,
  LinkedInIcon,
  TelegramIcon,
  MyPlaylistIcon,
} from "@/ui/components/icons/social";
import { validateSocialUrl } from "@/schemas/profileSchemas";

export interface SocialLink {
  platform: string;
  url: string;
  visible: boolean;
  createdAt: string;
}

export interface SocialLinksSectionProps {
  socialLinks: SocialLink[];
  onAddSocialLink: (platform: string, url: string) => Promise<void>;
  onDeleteSocialLink: (platform: string) => Promise<void>;
  onToggleVisibility: (platform: string) => void;
  canAddSocialLink: (platform: string) => boolean;
  isLoading?: boolean;
}

const PLATFORMS = [
  "phone",
  "email",
  "twitter",
  "instagram",
  "linkedin",
  "telegram",
  "myplaylist",
] as const;

const PLATFORM_LABELS = {
  email: "Email",
  phone: "Phone",
  twitter: "Twitter (X)",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  telegram: "Telegram",
  myplaylist: "My Playlist",
} as const;

const platformIcons: Record<string, React.ReactNode> = {
  email: <EmailIcon size={16} />,
  phone: <PhoneIcon size={16} />,
  twitter: <XIcon size={16} />,
  instagram: <InstagramIcon size={16} />,
  linkedin: <LinkedInIcon size={16} />,
  telegram: <TelegramIcon size={16} />,
  myplaylist: <MyPlaylistIcon size={16} />,
};

/**
 * SocialLinksSection Component
 *
 * Manages social media links with platform-specific icons and visibility controls.
 * Features:
 * - One link per platform enforcement
 * - Platform-specific icons (Email, Phone, X/Twitter, Instagram, LinkedIn, Telegram, My Playlist)
 * - Auto-save on blur
 * - Inline visibility toggle next to input field
 * - Help tooltip in header for each platform
 * - Delete confirmation modal
 *
 * Story 3.4.d: Replaced Spotify with MyPlaylist (multi-platform music links)
 *
 * Layout:
 * - Header: [Platform icon + label] [HelpTooltip]
 * - Input: [SocialLinkInput field] [Visibility Toggle (when URL exists)]
 * - Error: [Validation message if any]
 *
 * @example
 * ```tsx
 * <SocialLinksSection
 *   socialLinks={links}
 *   onAddSocialLink={handleAddLink}
 *   onDeleteSocialLink={handleDeleteLink}
 *   onToggleVisibility={handleToggleVisibility}
 *   canAddSocialLink={(platform) => !links.some(l => l.platform === platform)}
 * />
 * ```
 */
export function SocialLinksSection({
  socialLinks,
  onAddSocialLink,
  onDeleteSocialLink: _onDeleteSocialLink,
  onToggleVisibility,
  canAddSocialLink: _canAddSocialLink,
  isLoading = false,
}: SocialLinksSectionProps) {
  // Local state for input values during typing
  const [localValues, setLocalValues] = useState<Record<string, string>>({});

  // Validation error state
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});

  // Sync local state with backend social link data when it changes
  // BUT: Don't sync during save operations to prevent overwriting user input
  useEffect(() => {
    if (isLoading) return; // Preserve local state during save

    const initialValues: Record<string, string> = {};
    socialLinks.forEach((link) => {
      initialValues[link.platform] = link.url || "";
    });
    setLocalValues(initialValues);
  }, [socialLinks, isLoading]);

  const handleUrlBlur = async (platform: string, url: string) => {
    // Clear error if URL is empty
    if (!url) {
      setValidationErrors((prev) => ({ ...prev, [platform]: undefined }));
      // Proceed with API call to save empty value if link exists
      const link = socialLinks.find((l) => l.platform === platform);
      if (link) {
        await onAddSocialLink(platform, url);
      }
      return;
    }

    // Validate URL for platform
    const validation = validateSocialUrl(platform, url);

    if (!validation.success) {
      setValidationErrors((prev) => ({
        ...prev,
        [platform]: validation.error,
      }));
      return; // Don't call API if validation fails
    } else {
      setValidationErrors((prev) => ({ ...prev, [platform]: undefined }));
    }

    // Proceed with API call (existing logic)
    const link = socialLinks.find((l) => l.platform === platform);
    if (link && link.url !== url) {
      // Update existing link (allow empty to save empty value)
      await onAddSocialLink(platform, url);
    } else if (!link && url) {
      // Add new link only if URL is not empty
      await onAddSocialLink(platform, url);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Social Links List - Show ALL platforms */}
      {PLATFORMS.map((platform) => {
        const link = socialLinks.find((l) => l.platform === platform);
        const hasError = !!validationErrors[platform];
        return (
          <div key={platform} className="flex flex-col gap-2">
            {/* Header with platform icon and label (no HelpTooltip) */}

            {/* Input row with inline visibility toggle */}
            <div className="flex items-start gap-2">
              {/* URL Input - flex-1 to take available space */}
              <div className="flex-1">
                <SocialLinkInput
                  leftIcon={platformIcons[platform]}
                  value={localValues[platform] ?? link?.url ?? ""}
                  placeholder={
                    platform === "email"
                      ? "Enter email address"
                      : platform === "phone"
                      ? "Enter phone number"
                      : `Enter ${PLATFORM_LABELS[platform]} URL`
                  }
                  onChange={(e) =>
                    setLocalValues((prev) => ({
                      ...prev,
                      [platform]: e.target.value,
                    }))
                  }
                  onBlur={(e) => handleUrlBlur(platform, e.target.value)}
                />
              </div>

              {/* Visibility Toggle - inline on the right, only show when URL exists */}

              <div className="pt-1">
                <VisibilityToggle
                  visible={link?.visible || false}
                  onToggle={() => onToggleVisibility(platform)}
                  showLabel={false}
                  disabled={!link?.url}
                />
              </div>
            </div>

            {/* Validation Error - below the input */}
            {hasError && (
              <p className="text-danger text-sm pl-2">
                {validationErrors[platform]}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
